import { GetFilesProps } from '@/hooks/useFiles';
import { useAuth } from '@/state/auth';
import { GraphQuery, MyResourceKey, UniqueQueryKeys } from '@/types';

export const getQueryKey = {
  getAgents: (variant?: 'basic' | 'enhanced') =>
    [UniqueQueryKeys.AGENTS, ...(variant ? [variant] : [])] as const,
  getEnrich: (type: string, id: string) => [UniqueQueryKeys.ENRICH, type, id],
  getMy: (
    key: MyResourceKey,
    compositeKey?: string,
    to?: string,
    desc?: boolean,
    global?: boolean,
    allTenants?: boolean,
    osint?: boolean,
    praetorian?: boolean
  ) => {
    return [
      UniqueQueryKeys.MY,
      key,
      ...(compositeKey ? [compositeKey] : []),
      ...(to ? [to] : []),
      ...(desc ? [desc] : []),
      ...(global ? [global] : []),
      ...(allTenants ? [allTenants] : []),
      ...(osint ? [osint] : []),
      ...(praetorian ? [praetorian] : []),
    ];
  },
  // ENG-4404: descendant-tree discovery keys. Deliberately DISJOINT from
  // getMy keys — useMy entries are infinite-query shaped ({pages}), these are
  // plain useQueries entries; sharing a key shape would collide two data
  // shapes in one cache slot. The [MY, 'conversation-descendants', anchorId]
  // prefix is the single WS-invalidation target for the whole family.
  getDescendantConversations: (
    anchorId: string,
    unit:
      | { kind: 'parent'; nodeId: string; partition: 'user' | 'tenant' }
      | { kind: 'hunt'; huntId: string }
  ) => [
    UniqueQueryKeys.MY,
    'conversation-descendants',
    anchorId,
    ...(unit.kind === 'parent'
      ? ['parent', unit.nodeId, unit.partition]
      : ['hunt', unit.huntId]),
  ],
  // ENG-4404: upward root-resolution keys. Same disjoint-from-getMy discipline
  // as getDescendantConversations (plain useQueries entries, not {pages}).
  getConversationAncestor: (id: string, partition: 'user' | 'tenant') => [
    UniqueQueryKeys.MY,
    'conversation-ancestor',
    id,
    partition,
  ],
  getAccountAlerts: (account: string) => [
    UniqueQueryKeys.ACCOUNT_ALERTS,
    account,
  ],
  getBackend: () => [UniqueQueryKeys.Backends],
  getFile: (props: GetFilesProps) => [
    UniqueQueryKeys.GET_FILE,
    props.name,
    props.global,
  ],
  genericSearch: (
    search: string,
    desc?: boolean,
    global?: boolean,
    email?: string,
    allTenants?: boolean
  ) => [
    UniqueQueryKeys.GENERIC_MY_SEARCH,
    ...(search ? [search] : []),
    ...(desc !== undefined ? [String(desc)] : []),
    ...(global ? [global] : []),
    ...(email ? [email] : []),
    ...(allTenants ? [allTenants] : []),
  ],
  getGavatarProfilePicture: (email: string) => [
    UniqueQueryKeys.GAVATAR_PROFILE_PICTURE,
    email,
  ],
  getKev: () => [UniqueQueryKeys.KEV],
  userAttributes: () => [UniqueQueryKeys.USER_ATTRIBUTES],
  getSeedFilters: ({
    type,
    column,
  }: {
    type: 'domain' | 'ip';
    column: string;
  }) => [UniqueQueryKeys.SEED_FILTERS, type, column],
  getGraphQuery: (query: GraphQuery) => [UniqueQueryKeys.GRAPH_QUERY, query],
  getCapabilities: (filters?: {
    name?: string;
    target?: string;
    executor?: string;
  }) => [
    UniqueQueryKeys.CAPABILITIES,
    ...(filters ? [JSON.stringify(filters)] : []),
  ],
  getApiKeys: () => [UniqueQueryKeys.API_KEYS],
  getPlextracReports: (customerEmail?: string) => [
    UniqueQueryKeys.PLEXTAC_REPORTS,
    ...(customerEmail ? [customerEmail] : []),
  ],
  getReporting: (clientId?: string, customerEmail?: string) => [
    UniqueQueryKeys.REPORTING,
    ...(clientId ? [clientId] : []),
    ...(customerEmail ? [customerEmail] : []),
  ],
  getEvidenceFiles: (riskKey: string) => [
    UniqueQueryKeys.EVIDENCE_FILES,
    riskKey,
  ],
  getCollaboratorQuery: (key: string, email: string) => [
    UniqueQueryKeys.COLLABORATOR_QUERY,
    key,
    email,
  ],
  getPlexTracClientId: (customerEmail?: string) => [
    UniqueQueryKeys.PLEXTAC_CLIENT_ID,
    ...(customerEmail ? [customerEmail] : []),
  ],
  getAegisManagementTasks: (agentId?: string) => [
    UniqueQueryKeys.AEGIS_MANAGEMENT_TASKS,
    ...(agentId ? [agentId] : []),
  ],
  getAIBudget: () => [UniqueQueryKeys.AI_BUDGET],
  csfAssessment: () => ['csf-assessment'] as const,
  assetMetrics: (assetKey: string) => [UniqueQueryKeys.ASSET_METRICS, assetKey],
  aggregatedAssetMetrics: (assetKeys: readonly string[]) => [
    UniqueQueryKeys.ASSET_METRICS,
    'aggregate',
    ...assetKeys,
  ],
  constantineManifest: () => [UniqueQueryKeys.CONSTANTINE_MANIFEST],
  getConversationCost: (conversationId: string) => [
    UniqueQueryKeys.CONVERSATION_COST,
    conversationId,
  ],
  getHuntCost: (huntUuid: string) => [UniqueQueryKeys.HUNT_COST, huntUuid],
  getShares: (email?: string) => [
    UniqueQueryKeys.SHARES,
    ...(email ? [email] : []),
  ],
  getShareResolve: (token?: string) => [
    UniqueQueryKeys.SHARE_RESOLVE,
    ...(token ? [token] : []),
  ],
  // Tenant-deletion resource (global, Praetorian-Admin cross-tenant list).
  // The detail key nests under the list key so invalidating the list prefix
  // also refreshes any open detail query.
  getTenantDeletions: () => [UniqueQueryKeys.TENANT_DELETIONS] as const,
  getTenantDeletion: (id: string) =>
    [UniqueQueryKeys.TENANT_DELETIONS, id] as const,
};

/**
 * The user-scope segment `useGetUserKey` appends as the LAST query-key
 * segment, or `''` when it appends nothing. Exposed separately so callers
 * (useMy) can hand the exact suffix to the friend-aware invalidation matcher
 * in `@/utils/api` without replicating the precedence rules here (ENG-4400).
 */
export function useUserKeySuffix(
  doNotImpersonate?: boolean,
  email?: string
): string {
  const { friend } = useAuth();

  if (!doNotImpersonate && friend) {
    return friend;
  }

  return email ?? '';
}

export function useGetUserKey(
  key: ReadonlyArray<unknown> = [],
  doNotImpersonate?: boolean,
  email?: string
) {
  const suffix = useUserKeySuffix(doNotImpersonate, email);

  if (suffix) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return [...key, suffix] as any;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return [...key] as any;
}
